import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LOGO_URL } from "../lib/brand";
import {
  LogOut, Inbox, BarChart3, Users, Plus, Trash2, Save, RefreshCw,
  Mail as MailIcon, Link2, User, X, Edit2, UserPlus, KeyRound,
  Check, AlertCircle, MessageSquare, Hash, CreditCard, Phone, Cake, MapPin,
  Search, GitBranch, ChevronRight, Send, Loader2, MessageCircle,
} from "lucide-react";
import AdminTopNav from "../components/AdminTopNav";
import PartnerReachModal from "../components/admin/PartnerReachModal";
import UpcomingPartnerMails from "../components/admin/UpcomingPartnerMails";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

const emptyPartner = { name: "", email: "", phone: "", callmebot_apikey: "", ref_link: "", slug: "", roboforex_partneraccount: "", roboforex_ref_url: "", mt5_partner_id: "", birthdate: "", referrer_partner_id: "" };

export default function AdminPartners() {
  const [items, setItems] = useState([]);
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // partner object or {new:true}
  const [draft, setDraft] = useState(emptyPartner);
  const [saving, setSaving] = useState(false);
  // Partner-Reach modal — opened via the per-row WhatsApp button.
  const [reachPartner, setReachPartner] = useState(null);
  const [signupBusy, setSignupBusy] = useState(null); // signup id currently being processed
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("alphalane_admin_token") : null;

  const fetchAll = async () => {
    if (!token) return navigate("/admin/login");
    setLoading(true);
    try {
      const headers = { "x-admin-token": token };
      const [pRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/partners`, { headers }),
        axios.get(`${API}/admin/partner-signups`, { headers, params: { status: "pending" } }),
      ]);
      setItems(pRes.data);
      setSignups(sRes.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("alphalane_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Konnte Partner nicht laden.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const startNew = () => {
    setEditing({ new: true });
    setDraft(emptyPartner);
  };
  const startEdit = (p) => {
    setEditing(p);
    setDraft({ name: p.name, email: p.email, phone: p.phone || "", callmebot_apikey: p.callmebot_apikey || "", ref_link: p.ref_link || "", slug: p.slug || "", roboforex_partneraccount: p.roboforex_partneraccount || "", roboforex_ref_url: p.roboforex_ref_url || "", mt5_partner_id: p.mt5_partner_id || "", birthdate: p.birthdate || "", referrer_partner_id: p.referrer_partner_id || "" });
  };
  const cancel = () => {
    setEditing(null);
    setDraft(emptyPartner);
  };

  const save = async () => {
    if (!draft.name.trim() || !draft.email.trim()) {
      toast.error("Name und E-Mail sind Pflicht.");
      return;
    }
    setSaving(true);
    try {
      const refId = (draft.referrer_partner_id || "").trim();
      const refName = refId ? (items.find((p) => p.id === refId)?.name || null) : null;
      const body = {
        name: draft.name.trim(),
        email: draft.email.trim(),
        ref_link: draft.ref_link.trim() || null,
        phone: (draft.phone || "").trim() || null,
        callmebot_apikey: (draft.callmebot_apikey || "").trim() || null,
        slug: (draft.slug || "").trim().toLowerCase() || null,
        roboforex_partneraccount: (draft.roboforex_partneraccount || "").trim() || null,
        roboforex_ref_url: (draft.roboforex_ref_url || "").trim() || null,
        mt5_partner_id: (draft.mt5_partner_id || "").trim() || null,
        birthdate: (draft.birthdate || "").trim() || null,
        referrer_partner_id: refId || null,
        referrer_name: refName,
      };
      if (editing?.new) {
        await axios.post(`${API}/admin/partners`, body, { headers: { "x-admin-token": token } });
        toast.success("Partner angelegt.");
      } else {
        await axios.put(`${API}/admin/partners/${editing.id}`, body, { headers: { "x-admin-token": token } });
        toast.success("Partner aktualisiert.");
      }
      cancel();
      await fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Speichern fehlgeschlagen.";
      toast.error(typeof msg === "string" ? msg : "Validierungsfehler.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Partner "${p.name}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/admin/partners/${p.id}`, { headers: { "x-admin-token": token } });
      setItems((xs) => xs.filter((x) => x.id !== p.id));
      toast.success("Partner gelöscht.");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const resetPassword = async (p) => {
    if (!window.confirm(
      `Passwort für "${p.name}" (${p.email || "keine E-Mail"}) zurücksetzen?\n\n` +
      `→ Neues Passwort: ${p.slug}2026\n` +
      `→ Password-Reset-Mail wird automatisch verschickt.`
    )) return;
    try {
      const res = await axios.post(
        `${API}/admin/partners/${p.id}/reset-password`,
        {},
        { headers: { "x-admin-token": token } },
      );
      const s = res.data?.summary || {};
      const newPw = s.password || `${p.slug}2026`;
      const msgId = s.message_id ? ` (Resend-ID: ${s.message_id.slice(0, 8)}…)` : "";
      // Always show the password + copy/WhatsApp fallback regardless of
      // email_sent, so admin has an out-of-band channel if the mail gets
      // filtered/lost in spam.
      const waText = encodeURIComponent(
        `Hi ${p.name?.split(" ")[0] || ""}, dein neues Alphalane-Partner-Passwort: ${newPw}\n` +
        `Login: https://alphalane-tradingsystems.com/partner/login\n` +
        `(Beim ersten Login wirst du gebeten, ein eigenes Passwort zu wählen.)`,
      );
      const waPhone = (p.phone || "").replace(/[^0-9]/g, "");
      const waUrl = waPhone
        ? `https://wa.me/${waPhone}?text=${waText}`
        : `https://wa.me/?text=${waText}`;
      toast.custom(
        (id) => (
          <div className="bg-white border border-violet-200 rounded-xl shadow-lg p-4 max-w-md" data-testid="partner-reset-pw-toast">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center text-lg">🔐</div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-slate-900">
                  {s.email_sent ? "Passwort zurückgesetzt + Mail versandt" : "Passwort zurückgesetzt"}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {s.email_sent
                    ? `Mail an ${p.email}${msgId}`
                    : `⚠️ Mail NICHT versandt (${s.reason || "unbekannt"}) — bitte manuell weitergeben`}
                </p>
                <div className="mt-2 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                  <code data-testid="partner-reset-pw-value" className="text-sm font-mono text-slate-900 flex-1 select-all">{newPw}</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(newPw); toast.success("Passwort kopiert"); }}
                    data-testid="partner-reset-pw-copy"
                    className="text-[11px] uppercase tracking-wider font-semibold text-violet-600 hover:text-violet-800"
                  >
                    Kopieren
                  </button>
                </div>
                <div className="mt-2 flex gap-2">
                  <a
                    href={waUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="partner-reset-pw-whatsapp"
                    className="text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1.5 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  >
                    Per WhatsApp senden
                  </a>
                  <button
                    onClick={() => toast.dismiss(id)}
                    className="text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1.5 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 ml-auto"
                  >
                    Schliessen
                  </button>
                </div>
              </div>
            </div>
          </div>
        ),
        { duration: 30000 },
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Reset fehlgeschlagen.");
    }
  };

  const toggleNfcCard = async (p) => {
    const isMarked = !!p.nfc_card_sent_at;
    const question = isMarked
      ? `NFC-Karten-Versand für "${p.name}" zurücksetzen?\n(aktuell als versendet markiert am ${new Date(p.nfc_card_sent_at).toLocaleString("de-DE")})`
      : `NFC-Karte an "${p.name}" als VERSENDET markieren?`;
    if (!window.confirm(question)) return;
    try {
      const res = await axios.post(
        `${API}/admin/partners/${p.id}/toggle-nfc-card`,
        {},
        { headers: { "x-admin-token": token } },
      );
      const newTs = res.data?.nfc_card_sent_at || null;
      setItems((xs) => xs.map((x) => (x.id === p.id ? { ...x, nfc_card_sent_at: newTs } : x)));
      toast.success(newTs ? `NFC-Karte für ${p.name} als versendet markiert.` : `NFC-Markierung für ${p.name} zurückgesetzt.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "NFC-Toggle fehlgeschlagen.");
    }
  };

  const logout = () => {
    localStorage.removeItem("alphalane_admin_token");
    navigate("/admin/login");
  };

  const approveSignup = async (s) => {
    if (!window.confirm(`Bewerbung von ${s.name} (${s.email}) genehmigen?\n\n→ Partner-Account wird angelegt\n→ Welcome-Mail mit Login-Daten geht raus`)) return;
    setSignupBusy(s.id);
    try {
      const res = await axios.post(
        `${API}/admin/partner-signups/${s.id}/approve`,
        {},
        { headers: { "x-admin-token": token } },
      );
      toast.success(`✅ ${s.name} freigeschaltet — Welcome-Mail unterwegs.`);
      if (res?.data?.login_summary?.email_sent === false) {
        toast.warning("Partner angelegt, aber Welcome-Mail konnte nicht versendet werden. Bitte manuell auslösen.");
      }
      await fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Genehmigung fehlgeschlagen.");
    } finally {
      setSignupBusy(null);
    }
  };

  const rejectSignup = async (s) => {
    if (!window.confirm(`Bewerbung von ${s.name} ablehnen?`)) return;
    setSignupBusy(s.id);
    try {
      await axios.post(
        `${API}/admin/partner-signups/${s.id}/reject`,
        {},
        { headers: { "x-admin-token": token } },
      );
      toast.success("Bewerbung abgelehnt.");
      await fetchAll();
    } catch {
      toast.error("Ablehnung fehlgeschlagen.");
    } finally {
      setSignupBusy(null);
    }
  };

  const deleteSignup = async (s) => {
    if (!window.confirm(`Bewerbung von ${s.name} dauerhaft löschen?`)) return;
    setSignupBusy(s.id);
    try {
      await axios.delete(`${API}/admin/partner-signups/${s.id}`, { headers: { "x-admin-token": token } });
      toast.success("Bewerbung gelöscht.");
      await fetchAll();
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    } finally {
      setSignupBusy(null);
    }
  };

  // Filtered partner list — case-insensitive name/email/slug search
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) =>
      [(p.name || ""), (p.email || ""), (p.slug || ""), (p.roboforex_partneraccount || "")]
        .some((v) => v.toLowerCase().includes(q)),
    );
  }, [items, query]);

  // Partner tree as a flat ordered list (parent → child sequence) with depth
  // info so the renderer can apply indentation without recursive JSX. This
  // avoids a known visual-edits Babel-plugin stack-overflow with recursive
  // self-referencing components.
  const treeFlat = useMemo(() => {
    const byId = new Map(items.map((p) => [p.id, { ...p, _children: [] }]));
    const roots = [];
    for (const p of byId.values()) {
      const parent = p.referrer_partner_id ? byId.get(p.referrer_partner_id) : null;
      if (parent) parent._children.push(p);
      else roots.push(p);
    }
    const sortByName = (a, b) => (a.name || "").localeCompare(b.name || "");
    // Compute total downline size (children + grandchildren + ...) per node.
    const computeDownlineSize = (node) => {
      let total = 0;
      for (const c of node._children) total += 1 + computeDownlineSize(c);
      return total;
    };
    for (const node of byId.values()) {
      node._downline_size = computeDownlineSize(node);
    }
    const out = [];
    const stack = [...roots].sort(sortByName).map((n) => ({ node: n, depth: 0, isLast: false }));
    // DFS iterative — preserves visual order
    const expand = (entry) => {
      out.push({
        ...entry.node,
        depth: entry.depth,
        hasChildren: entry.node._children.length > 0,
        downlineSize: entry.node._downline_size,
        referrerName: entry.node.referrer_partner_id ? (byId.get(entry.node.referrer_partner_id)?.name || entry.node.referrer_name) : entry.node.referrer_name,
      });
      const kids = [...entry.node._children].sort(sortByName);
      for (let i = kids.length - 1; i >= 0; i--) {
        stack.unshift({ node: kids[i], depth: entry.depth + 1, isLast: i === kids.length - 1 });
      }
    };
    while (stack.length) {
      const next = stack.shift();
      expand(next);
    }
    return out;
  }, [items]);

  // Are there any partners with a referrer? Used for the "no hierarchy yet" hint.
  const [collapsedNodes, setCollapsedNodes] = useState(() => new Set());
  const toggleNode = (id) => {
    setCollapsedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // Filter out children whose ancestor is collapsed.
  const visibleTree = useMemo(() => {
    if (collapsedNodes.size === 0) return treeFlat;
    const hiddenParents = new Set(collapsedNodes);
    const result = [];
    // We walk in DFS order. Whenever we hit a collapsed node, we skip any
    // following nodes whose depth is strictly greater until we drop back to
    // its level (or shallower).
    let skipDepth = -1;
    for (const node of treeFlat) {
      if (skipDepth !== -1) {
        if (node.depth > skipDepth) continue; // hidden descendant
        skipDepth = -1; // back to sibling/ancestor level → stop skipping
      }
      result.push(node);
      if (hiddenParents.has(node.id) && node.hasChildren) {
        skipDepth = node.depth;
      }
    }
    return result;
  }, [treeFlat, collapsedNodes]);

  const hasAnyHierarchy = useMemo(
    () => items.some((p) => p.referrer_partner_id),
    [items],
  );

  return (
    <div className="min-h-screen bg-[var(--al-bg)]">
      <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
            <div className="font-display font-semibold text-[15px]">
              <span className="al-grad-text">ALPHALANE</span>
              <span className="text-slate-500 text-xs ml-2 font-medium uppercase tracking-widest">Admin</span>
            </div>
          </Link>
          <button onClick={logout} data-testid="admin-logout" className="al-cta-ghost !py-2 !px-3 text-sm">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <AdminTopNav />

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <span className="al-tag">Stammdaten</span>
            <h1 className="mt-3 font-display font-bold text-3xl sm:text-4xl">
              Partner & <span className="al-grad-text">Reflinks.</span>
            </h1>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Verwalte deine Empfehlungs-Partner. Der Name erscheint im Kontaktformular als Autocomplete-Vorschlag,
              sobald ein Kunde „Empfehlung" auswählt und mindestens 2 Buchstaben tippt. Die hinterlegte Partner-E-Mail
              erhält dann automatisch die Anfrage-Benachrichtigung statt deiner Standard-Adresse.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchAll} data-testid="partners-reload" className="al-cta-ghost !py-2 !px-3 text-sm">
              <RefreshCw className="h-4 w-4" /> Neu laden
            </button>
            <button onClick={startNew} data-testid="partners-add" className="al-cta !py-2.5 !px-5 text-sm" disabled={editing?.new}>
              <Plus className="h-4 w-4" /> Neuer Partner
            </button>
          </div>
        </div>

        {/* Pending partner-signups (from /partneronboarding) */}
        {signups.length > 0 && (
          <section className="al-card p-6 mb-6 border-amber-200 bg-gradient-to-br from-amber-50/40 to-white" data-testid="partner-signups-panel">
            <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-[10px] uppercase tracking-widest font-bold">
                  <AlertCircle className="h-3 w-3" /> Offene Bewerbungen · {signups.length}
                </span>
                <h2 className="mt-2 font-display font-bold text-xl">Partner-Bewerbungen</h2>
                <p className="text-sm text-slate-600 mt-1">
                  Eingegangen über <a href="/partneronboarding" target="_blank" rel="noreferrer" className="text-[var(--al-purple)] hover:underline">/partneronboarding</a>.
                  Klick „Freischalten", um den Partner anzulegen — die Welcome-Mail mit Login-Daten geht automatisch raus.
                </p>
              </div>
            </div>
            <div className="space-y-3">
              {signups.map((s) => (
                <SignupCard
                  key={s.id}
                  s={s}
                  busy={signupBusy === s.id}
                  onApprove={() => approveSignup(s)}
                  onReject={() => rejectSignup(s)}
                  onDelete={() => deleteSignup(s)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Editor */}
        {editing && (
          <div className="al-card p-6 mb-6" data-testid="partner-editor">
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="font-display font-semibold text-lg">
                {editing.new ? "Neuen Partner anlegen" : `„${editing.name}" bearbeiten`}
              </h3>
              <button onClick={cancel} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <FormField icon={<User className="h-3.5 w-3.5" />} label="Name *">
                <input
                  data-testid="partner-input-name"
                  className="al-input-sm"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Pascal Schwab"
                />
              </FormField>
              <FormField icon={<MailIcon className="h-3.5 w-3.5" />} label="E-Mail *">
                <input
                  data-testid="partner-input-email"
                  type="email"
                  className="al-input-sm"
                  value={draft.email}
                  onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                  placeholder="pascal@moonshine-solutions.com"
                />
              </FormField>
              <FormField icon={<MailIcon className="h-3.5 w-3.5" />} label="Telefon (Onboarding-Support)">
                <input
                  data-testid="partner-input-phone"
                  type="tel"
                  className="al-input-sm"
                  value={draft.phone}
                  onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                  placeholder="+41 79 950 16 46"
                />
              </FormField>
              <FormField icon={<MailIcon className="h-3.5 w-3.5" />} label="CallMeBot API-Key (optional, für WhatsApp-Push bei Onboarding-Abschluss)">
                <input
                  data-testid="partner-input-callmebot"
                  type="text"
                  className="al-input-sm font-mono-tab"
                  value={draft.callmebot_apikey}
                  onChange={(e) => setDraft({ ...draft, callmebot_apikey: e.target.value })}
                  placeholder="z.B. 1234567 — leer = Push geht an Admin (zum Weiterleiten)"
                />
              </FormField>
              <FormField icon={<Link2 className="h-3.5 w-3.5" />} label="Affiliate-Code / Slug (RoboForex Agent-ID)" className="sm:col-span-2">
                <input
                  data-testid="partner-input-affiliate"
                  className="al-input-sm font-mono-tab"
                  value={draft.slug || draft.ref_link || ""}
                  onChange={(e) => {
                    const v = e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "");
                    setDraft({ ...draft, slug: v, ref_link: v });
                  }}
                  placeholder="z.B. fjpcq — leer lassen für automatische Generierung"
                  maxLength={20}
                />
                <div className="mt-2 text-[11px] text-slate-500 font-mono-tab leading-relaxed">
                  {(draft.slug || draft.ref_link) ? (
                    <>→ Register-URL: <span className="text-violet-700">/register/{draft.slug || draft.ref_link}</span> · Affiliate-Code: <span className="text-violet-700">{draft.slug || draft.ref_link}</span></>
                  ) : (
                    <>Ein einziger Code bestimmt sowohl die URL als auch die Affiliate-Zuordnung bei RoboForex.</>
                  )}
                </div>
              </FormField>
              <FormField label="Roboforex-Partneraccount-Nr.">
                <input
                  data-testid="partner-input-roboforex"
                  className="al-input-sm font-mono-tab"
                  value={draft.roboforex_partneraccount || ""}
                  onChange={(e) => setDraft({ ...draft, roboforex_partneraccount: e.target.value })}
                  placeholder="z.B. 30291457"
                  maxLength={40}
                />
              </FormField>
              <FormField label="Reflink Video 2 (Copy-Trading)" className="sm:col-span-2">
                <input
                  data-testid="partner-input-mt5-partner-id"
                  className="al-input-sm font-mono-tab text-xs"
                  value={draft.mt5_partner_id || ""}
                  onChange={(e) => setDraft({ ...draft, mt5_partner_id: e.target.value })}
                  placeholder="https://my.roboforex.com/en/register-copyfx/?trader_account=…&partner_token=…"
                  maxLength={500}
                />
              </FormField>
              <FormField label="Geburtsdatum (für automatische Gratulation)">
                <input
                  data-testid="partner-input-birthdate"
                  type="date"
                  className="al-input-sm"
                  value={draft.birthdate || ""}
                  onChange={(e) => setDraft({ ...draft, birthdate: e.target.value })}
                />
              </FormField>
              <FormField
                icon={<GitBranch className="h-3.5 w-3.5" />}
                label="Empfehlungsgeber (Upline)"
                className="sm:col-span-2"
              >
                <select
                  data-testid="partner-input-referrer"
                  className="al-input-sm"
                  value={draft.referrer_partner_id || ""}
                  onChange={(e) => setDraft({ ...draft, referrer_partner_id: e.target.value })}
                >
                  <option value="">— Kein Empfehlungsgeber (Top-Level) —</option>
                  {items
                    .filter((p) => !editing?.id || p.id !== editing.id)
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}{p.slug ? `  ·  ${p.slug}` : ""}
                      </option>
                    ))}
                </select>
                <p className="mt-1 text-[11px] text-slate-400 leading-relaxed">
                  Bestimmt, unter wem dieser Partner im Partner-Baum erscheint.
                </p>
              </FormField>
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={cancel} className="al-cta-ghost text-sm">Abbrechen</button>
              <button onClick={save} disabled={saving} data-testid="partner-save" className="al-cta text-sm">
                <Save className="h-4 w-4" /> {saving ? "Speichere…" : "Speichern"}
              </button>
            </div>
          </div>
        )}

        {/* Search */}
        {!loading && items.length > 0 && (
          <div className="al-card p-3 mb-4 flex items-center gap-3" data-testid="partners-search">
            <Search className="h-4 w-4 text-slate-400 ml-2 flex-shrink-0" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Suche nach Name, E-Mail, Slug oder RoboForex-Nr.…"
              data-testid="partners-search-input"
              className="flex-1 min-w-0 px-2 py-2 bg-transparent border-0 outline-none text-sm font-medium text-slate-800 placeholder:text-slate-400"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                data-testid="partners-search-clear"
                className="text-xs font-bold text-slate-400 hover:text-slate-700 inline-flex items-center gap-1 mr-2"
                title="Suche zurücksetzen"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            <span className="text-[11px] uppercase tracking-widest font-bold text-slate-400 mr-2 hidden sm:inline">
              {filtered.length} / {items.length}
            </span>
            <span
              data-testid="partners-nfc-summary"
              className="text-[11px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 mr-2 hidden md:inline-flex items-center gap-1"
              title="Partner, denen die NFC-Visitenkarte bereits zugesendet wurde"
            >
              <CreditCard className="h-3 w-3" />
              {items.filter((p) => p.nfc_card_sent_at).length} NFC versendet
            </span>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="al-card p-12 text-center text-slate-500">Lade Partner…</div>
        ) : items.length === 0 ? (
          <div className="al-card p-12 text-center" data-testid="partners-empty">
            <Users className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="mt-4 text-slate-600">Noch keine Partner angelegt.</p>
            <button onClick={startNew} className="mt-4 al-cta text-sm">
              <Plus className="h-4 w-4" /> Ersten Partner anlegen
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="al-card p-10 text-center" data-testid="partners-no-results">
            <Search className="h-8 w-8 text-slate-300 mx-auto" />
            <p className="mt-3 text-slate-600 text-sm">
              Kein Partner gefunden für „<strong>{query}</strong>".
            </p>
            <button onClick={() => setQuery("")} className="mt-3 al-cta-ghost text-xs">
              Suche zurücksetzen
            </button>
          </div>
        ) : (
          <div className="al-card overflow-hidden" data-testid="partners-table">
            <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-[var(--al-line)] bg-[var(--al-bg)] text-[11px] uppercase tracking-widest font-semibold text-slate-500">
              <div className="col-span-3">Name</div>
              <div className="col-span-3">E-Mail</div>
              <div className="col-span-2">Slug</div>
              <div className="col-span-2">RoboForex-Nr.</div>
              <div className="col-span-2 text-right">Aktion</div>
            </div>
            {filtered.map((p) => (
              <div key={p.id} data-testid={`partner-row-${p.id}`} className="grid grid-cols-12 gap-3 px-5 py-3 items-center border-b border-[var(--al-line)] last:border-b-0 text-sm hover:bg-slate-50 transition-colors">
                <div className="col-span-3 font-display font-semibold text-slate-900 truncate">{p.name}</div>
                <div className="col-span-3 text-slate-600 truncate font-mono-tab text-[13px]">{p.email}</div>
                <div className="col-span-2 truncate">
                  {p.slug ? (
                    <span className="font-mono-tab text-[13px] font-bold text-[var(--al-purple)]" title={`/register/${p.slug}`}>{p.slug}</span>
                  ) : (
                    <span className="text-slate-400 text-xs">—</span>
                  )}
                </div>
                <div className="col-span-2 truncate" data-testid={`partner-roboforex-${p.id}`}>
                  {p.roboforex_partneraccount ? (
                    <span className="font-mono-tab text-[13px] text-slate-800" title="RoboForex-Partneraccount-Nr.">{p.roboforex_partneraccount}</span>
                  ) : (
                    <span className="text-slate-400 text-xs">—</span>
                  )}
                </div>
                <div className="col-span-2 flex justify-end gap-1">
                  <button onClick={() => resetPassword(p)} data-testid={`partner-reset-pw-${p.id}`} className="p-1.5 rounded-md hover:bg-violet-50 text-violet-500" title="Passwort zurücksetzen + Mail senden">
                    <KeyRound className="h-4 w-4" />
                  </button>
                  <button onClick={() => startEdit(p)} data-testid={`partner-edit-${p.id}`} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" title="Bearbeiten">
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setReachPartner(p)}
                    disabled={!p.phone}
                    data-testid={`partner-reach-${p.id}`}
                    className="p-1.5 rounded-md hover:bg-emerald-50 text-emerald-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title={p.phone ? "WhatsApp-Nachricht senden" : "Keine Telefon-Nummer hinterlegt"}
                  >
                    <MessageCircle className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => toggleNfcCard(p)}
                    data-testid={`partner-nfc-${p.id}`}
                    className={`p-1.5 rounded-md transition-colors ${
                      p.nfc_card_sent_at
                        ? "bg-amber-100 hover:bg-amber-200 text-amber-700 ring-1 ring-amber-300"
                        : "hover:bg-amber-50 text-slate-400 hover:text-amber-600"
                    }`}
                    title={
                      p.nfc_card_sent_at
                        ? `NFC-Karte versendet am ${new Date(p.nfc_card_sent_at).toLocaleDateString("de-DE")} — Klick zum Zurücksetzen`
                        : "NFC-Karte als versendet markieren"
                    }
                  >
                    <CreditCard className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(p)} data-testid={`partner-delete-${p.id}`} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-500" title="Löschen">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Partner tree (downline / upline structure) */}
        {!loading && items.length > 0 && (
          <section className="al-card p-6 mt-8" data-testid="partner-tree">
            <div className="flex items-center gap-2 mb-1">
              <GitBranch className="h-4 w-4 text-[var(--al-purple)]" />
              <h2 className="font-display font-bold text-lg">Partner-Struktur</h2>
              {hasAnyHierarchy && (
                <span className="font-mono-tab text-[11px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5 ml-2 border border-emerald-200">
                  {items.filter((p) => p.referrer_partner_id).length} mit Empfehler
                </span>
              )}
            </div>
            <p className="text-sm text-slate-600 mb-5">
              Wer wurde durch wen geworben? Hierarchie nach <code className="text-[var(--al-purple)] font-mono-tab">referrer_partner_id</code>. Eingerückte Partner sind Downline ihres Eltern-Knotens.
            </p>
            {!hasAnyHierarchy && (
              <div className="mb-5 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900 leading-relaxed" data-testid="partner-tree-empty-hint">
                <strong>Keine Hierarchie sichtbar — und das stimmt auch:</strong> aktuell hat <em>keiner</em> deiner {items.length} Partner einen Empfehler („referrer_partner_id") gesetzt. Sobald sich neue Partner über <code className="text-[var(--al-purple)] font-mono-tab">/partneronboarding/{"{slug}"}</code> bewerben, wird der Empfehler automatisch zugewiesen.
                <br /><br />
                <strong>Du kannst auch nachträglich einen Empfehler setzen</strong>: klick auf einen Partner in der Liste oben → Editor öffnet → Feld „Empfehlungsgeber (Upline)" → speichern. Sobald 2 Partner verknüpft sind, siehst du hier die Baumstruktur.
              </div>
            )}
            {treeFlat.length === 0 ? (
              <p className="text-sm text-slate-500">Noch keine Hierarchie vorhanden.</p>
            ) : (
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs text-slate-500">
                  {collapsedNodes.size > 0 && (
                    <span data-testid="partner-tree-collapsed-count">
                      {collapsedNodes.size} Knoten eingeklappt
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="tree-expand-all"
                    onClick={() => setCollapsedNodes(new Set())}
                    className="text-[10px] uppercase tracking-widest font-semibold text-violet-600 hover:text-violet-800 border border-violet-200 hover:bg-violet-50 rounded-full px-2 py-0.5 transition-colors"
                  >
                    Alle ausklappen
                  </button>
                  <button
                    type="button"
                    data-testid="tree-collapse-all"
                    onClick={() => {
                      const allWithKids = new Set(treeFlat.filter((n) => n.hasChildren).map((n) => n.id));
                      setCollapsedNodes(allWithKids);
                    }}
                    className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-full px-2 py-0.5 transition-colors"
                  >
                    Alle einklappen
                  </button>
                </div>
              </div>
            )}
            {treeFlat.length > 0 && (
              <ul className="space-y-0.5 text-sm border-l-2 border-slate-100 pl-2">
                {visibleTree.map((node) => {
                  const isCollapsed = collapsedNodes.has(node.id);
                  return (
                  <li key={node.id} data-testid={`tree-node-${node.id}`}>
                    <div
                      className={`flex items-center gap-2 py-2 px-2 rounded-md hover:bg-violet-50/50 transition-colors ${node.depth > 0 ? "border-l-2 border-violet-100 ml-2" : ""}`}
                      style={{ paddingLeft: `${node.depth * 22 + 8}px` }}
                    >
                      {node.depth > 0 && (
                        <span className="text-violet-300 text-xs select-none font-mono">└─</span>
                      )}
                      {node.hasChildren ? (
                        <button
                          type="button"
                          onClick={() => toggleNode(node.id)}
                          data-testid={`tree-toggle-${node.id}`}
                          className="h-5 w-5 flex-shrink-0 rounded hover:bg-violet-100 inline-flex items-center justify-center transition-colors"
                          aria-label={isCollapsed ? "Ausklappen" : "Einklappen"}
                          title={isCollapsed ? "Ausklappen" : "Einklappen"}
                        >
                          <ChevronRight className={`h-3.5 w-3.5 text-[var(--al-purple)] flex-shrink-0 transition-transform ${isCollapsed ? "" : "rotate-90"}`} />
                        </button>
                      ) : (
                        <span className="h-1.5 w-1.5 rounded-full bg-slate-300 flex-shrink-0 ml-1" />
                      )}
                      <span className="font-display font-semibold text-slate-900 truncate">
                        {node.name}
                      </span>
                      {node.slug && (
                        <span className="font-mono-tab text-[11px] font-bold text-[var(--al-purple)] bg-violet-50 rounded-full px-2 py-0.5 flex-shrink-0">
                          {node.slug}
                        </span>
                      )}
                      {node.depth === 0 && !node.referrer_partner_id && (
                        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 flex-shrink-0">Top-Level</span>
                      )}
                      {node.referrerName && node.depth > 0 && (
                        <span className="text-[11px] text-slate-500 italic truncate">← geworben von <strong className="not-italic font-display font-semibold text-slate-700">{node.referrerName}</strong></span>
                      )}
                      {node.downlineSize > 0 && (
                        <span className="ml-auto font-mono-tab text-[11px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5 flex-shrink-0 border border-emerald-200" title={`${node.downlineSize} Partner in der Downline (direkt + indirekt)`}>
                          ↓ {node.downlineSize}
                        </span>
                      )}
                    </div>
                  </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}

        {/* Partner-Mail-Übersicht — wer hat welche P-Mail bekommen? */}
        <PartnerMailOverview token={token} />

        {/* Nächste Mails — was kommt als NÄCHSTES auf welchen Partner zu? */}
        <UpcomingPartnerMails token={token} />
      </main>

      <style>{`
        .al-input-sm { width: 100%; padding: 9px 12px; border-radius: 10px; border: 1px solid var(--al-line); background: white; font-family: 'Manrope', sans-serif; font-size: 14px; color: var(--al-ink); transition: border-color 200ms, box-shadow 200ms; }
        .al-input-sm:focus { outline: none; border-color: var(--al-purple); box-shadow: 0 0 0 3px rgba(139,92,246,0.10); }
      `}</style>

      {reachPartner ? (
        <PartnerReachModal
          partner={reachPartner}
          onClose={() => setReachPartner(null)}
          onSent={() => setReachPartner(null)}
        />
      ) : null}
    </div>
  );
}

const FormField = ({ icon, label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5">
      {icon} {label}
    </span>
    {children}
  </label>
);

const MAIL_STATUS_STYLE = {
  sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  scheduled: "bg-amber-50 text-amber-700 border-amber-200",
  skipped: "bg-slate-100 text-slate-600 border-slate-200",
};

function fmtDt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return String(iso).slice(0, 19);
  }
}

const PartnerMailOverview = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [onlyWithMails, setOnlyWithMails] = useState(false);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    setErr("");
    try {
      const r = await axios.get(`${API}/admin/partners-mail-overview`, {
        headers: { "x-admin-token": token },
      });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Konnte Mail-Übersicht nicht laden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const partners = (data?.partners || []).filter((p) => !onlyWithMails || p.mails_count > 0);
  const totalMails = (data?.partners || []).reduce((s, p) => s + p.mails_count, 0);

  return (
    <section className="al-card p-6 mt-8" data-testid="partner-mail-overview">
      <div className="flex items-center gap-2 mb-1 flex-wrap justify-between">
        <div className="flex items-center gap-2">
          <MailIcon className="h-4 w-4 text-[var(--al-purple)]" />
          <h2 className="font-display font-bold text-lg">Mail-Übersicht pro Partner</h2>
          {data && (
            <span className="text-xs font-mono-tab text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
              {totalMails} Mails / {data.total} Partner
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600 inline-flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={onlyWithMails}
              onChange={(e) => setOnlyWithMails(e.target.checked)}
              data-testid="pmo-only-with-mails"
              className="accent-[var(--al-purple)]"
            />
            Nur Partner mit Mails
          </label>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="pmo-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
          </button>
        </div>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Welcher Partner hat welche Mail wann erhalten? Klick auf eine Zeile für Details (Status, Öffnungen, Klicks).
      </p>

      {err && (
        <div className="mb-3 p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">{err}</div>
      )}

      {loading && !data ? (
        <p className="text-sm text-slate-500">Lade …</p>
      ) : partners.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="pmo-empty">
          {onlyWithMails ? "Kein Partner hat bisher eine Mail erhalten." : "Keine Partner gefunden."}
        </p>
      ) : (
        <div className="border border-[var(--al-line)] rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-slate-50 text-[10px] uppercase tracking-widest font-bold text-slate-500 border-b border-[var(--al-line)]">
            <div className="col-span-3">Partner</div>
            <div className="col-span-3">E-Mail</div>
            <div className="col-span-1 text-center">Group</div>
            <div className="col-span-1 text-center">Mails</div>
            <div className="col-span-2">Sequenz-Start</div>
            <div className="col-span-2 text-right">Drip-Status</div>
          </div>
          {partners.map((p) => {
            const isOpen = expanded.has(p.id);
            return (
              <div key={p.id} data-testid={`pmo-row-${p.id}`} className="border-b border-[var(--al-line)] last:border-b-0">
                <button
                  type="button"
                  onClick={() => toggle(p.id)}
                  className="w-full grid grid-cols-12 gap-3 px-4 py-3 items-center text-sm hover:bg-slate-50 transition-colors text-left"
                >
                  <div className="col-span-3 font-display font-semibold text-slate-900 truncate inline-flex items-center gap-1.5">
                    <ChevronRight className={`h-3.5 w-3.5 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    {p.name}
                  </div>
                  <div className="col-span-3 text-slate-600 truncate font-mono-tab text-[12px]">{p.email}</div>
                  <div className="col-span-1 text-center">
                    {p.mail_group ? (
                      <span className="font-mono-tab text-[11px] font-bold text-[var(--al-purple)] bg-violet-50 rounded-full px-2 py-0.5">{p.mail_group}</span>
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </div>
                  <div className="col-span-1 text-center">
                    <span className={`font-mono-tab text-[11px] font-bold rounded-full px-2 py-0.5 ${p.mails_count > 0 ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {p.mails_count}
                    </span>
                  </div>
                  <div className="col-span-2 text-[12px] text-slate-500 font-mono-tab truncate">{fmtDt(p.mail_group_changed_at)}</div>
                  <div className="col-span-2 text-right">
                    {p.drip_opt_out ? (
                      <span className="text-[11px] font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5" title={p.drip_opt_out_reason || ""}>Pausiert</span>
                    ) : (
                      <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Aktiv</span>
                    )}
                  </div>
                </button>
                {isOpen && (
                  <div data-testid={`pmo-detail-${p.id}`} className="px-4 pb-4 bg-slate-50/50">
                    {p.mails.length === 0 ? (
                      <p className="text-xs text-slate-500 italic py-2">Noch keine Mails an diesen Partner versendet.</p>
                    ) : (
                      <table className="w-full text-[12px] mt-2">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                            <th className="text-left py-1.5 pl-2">Mail-ID</th>
                            <th className="text-left py-1.5">Betreff</th>
                            <th className="text-left py-1.5">Versendet</th>
                            <th className="text-left py-1.5">Geöffnet</th>
                            <th className="text-left py-1.5">Geklickt</th>
                            <th className="text-left py-1.5 pr-2">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.mails.map((m, i) => (
                            <tr key={`${m.mail_id}-${i}`} className="border-t border-[var(--al-line)]">
                              <td className="py-2 pl-2 font-mono-tab font-bold text-[var(--al-purple)]">{m.mail_id}</td>
                              <td className="py-2 text-slate-700 truncate max-w-[260px]" title={m.subject}>{m.subject || "—"}</td>
                              <td className="py-2 text-slate-600 font-mono-tab">{fmtDt(m.sent_at)}</td>
                              <td className="py-2 text-slate-600 font-mono-tab">{m.opened_at ? fmtDt(m.opened_at) : "—"}</td>
                              <td className="py-2 text-slate-600 font-mono-tab">{m.clicked_at ? fmtDt(m.clicked_at) : "—"}</td>
                              <td className="py-2 pr-2">
                                <span className={`inline-block text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 border ${MAIL_STATUS_STYLE[m.status] || MAIL_STATUS_STYLE.skipped}`}>
                                  {m.status || "—"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

const SignupCard = ({ s, busy, onApprove, onReject, onDelete }) => {
  const created = s.created_at
    ? new Date(s.created_at).toLocaleString("de-CH", { dateStyle: "medium", timeStyle: "short" })
    : "";
  return (
    <div
      data-testid={`signup-card-${s.id}`}
      className="rounded-xl border border-amber-200/70 bg-white p-5"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="font-display font-bold text-base text-slate-900">{s.name}</div>
          <div className="text-xs text-slate-500 font-mono-tab">{created}</div>
        </div>
        <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-[10px] uppercase tracking-widest font-bold">
          Pending
        </span>
      </div>

      <div className="grid sm:grid-cols-2 gap-x-5 gap-y-2 text-sm">
        <Row icon={<MailIcon className="h-3.5 w-3.5" />} label="E-Mail" value={
          <a href={`mailto:${s.email}`} className="text-[var(--al-purple)] hover:underline font-mono-tab text-[13px]">{s.email}</a>
        } />
        <Row icon={<Phone className="h-3.5 w-3.5" />} label="Telefon" value={
          <a href={`tel:${s.phone}`} className="text-slate-700 font-mono-tab text-[13px] hover:text-[var(--al-purple)]">{s.phone || "—"}</a>
        } />
        <Row icon={<Hash className="h-3.5 w-3.5" />} label="Affiliate-Code · Agent ID" value={
          <code className="text-slate-900 font-mono-tab font-bold">{s.affiliate_code || "—"}</code>
        } />
        <Row icon={<CreditCard className="h-3.5 w-3.5" />} label="MT5-Account (Payouts)" value={
          s.roboforex_account ? <code className="text-slate-900 font-mono-tab">{s.roboforex_account}</code> : <span className="text-slate-400">—</span>
        } />
        <Row icon={<User className="h-3.5 w-3.5" />} label="Empfehlungsgeber" value={s.referrer_name || <span className="text-slate-400">—</span>} />
        <Row icon={<Cake className="h-3.5 w-3.5" />} label="Geburtsdatum" value={s.birthdate || <span className="text-slate-400">—</span>} />
        <Row icon={<MapPin className="h-3.5 w-3.5" />} label="Adresse" value={s.address || <span className="text-slate-400">—</span>} />
        {s.callmebot_apikey && (
          <Row icon={<KeyRound className="h-3.5 w-3.5" />} label="CallMeBot-Key" value={<code className="text-slate-700 font-mono-tab text-xs">{s.callmebot_apikey}</code>} />
        )}
      </div>

      {s.notes && (
        <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
            <MessageSquare className="h-3 w-3" /> Notiz
          </div>
          {s.notes}
        </div>
      )}

      <div className="mt-4 flex gap-2 flex-wrap">
        <button
          onClick={onApprove}
          disabled={busy}
          data-testid={`signup-approve-${s.id}`}
          className="al-cta text-sm !py-2 !px-4 disabled:opacity-60"
        >
          <Check className="h-4 w-4" /> {busy ? "…" : "Freischalten + Mail senden"}
        </button>
        <button
          onClick={onReject}
          disabled={busy}
          data-testid={`signup-reject-${s.id}`}
          className="al-cta-ghost text-sm !py-2 !px-4 disabled:opacity-60"
        >
          <X className="h-4 w-4" /> Ablehnen
        </button>
        <button
          onClick={onDelete}
          disabled={busy}
          data-testid={`signup-delete-${s.id}`}
          className="al-cta-ghost text-sm !py-2 !px-3 disabled:opacity-60 text-rose-500 hover:text-rose-700"
          title="Dauerhaft löschen"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const Row = ({ icon, label, value }) => (
  <div>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-0.5">
      {icon} {label}
    </div>
    <div className="text-slate-800">{value}</div>
  </div>
);

